import * as React from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from '@sunbit-dev/translations';
import { StateCodes } from '@sunbit-dev/common/enums/State';
import Originators from '@sunbit-dev/common/enums/Originators';
import Verticals from '@sunbit-dev/common/enums/Verticals';
import { Top } from '@sunbit-dev/purchase-common/src/components/top/Top';
import GetStarted from '../GetStarted/GetStarted';
import PoweredBy from '../PoweredBy/NewPoweredBy';
import ArrowDown from '../ArrowDown/ArrowDown';
import Reviews from '../Reviews/Reviews';

import creditCheck from '../../assets/credit-check.svg';
import thumbsUp from '../../assets/thumbs-up.svg';
import lightningFast from '../../assets/lightningFast2.svg';
import circle1 from '../../assets/circle1.svg';
import triangle1 from '../../assets/triangle1.svg';
import line1 from '../../assets/line1.svg';
import triangle2 from '../../assets/triangle2.svg';

import { BulletLine, ConnectedBulletsList, OnlineButton, TitleV2 as Title, ButtonSize, HelpText } from '@sunbit-dev/web-ui-lib';
import { useCallback, useState, useMemo } from 'react';
import { SupportPopup } from '@sunbit-dev/purchase-common/src/components/supportPopup/SupportPopup';
import { PrivacyAllowanceLayer } from '@sunbit-dev/purchase-common/src/components/privacyAllowanceLayer/PrivacyAllowanceLayer';

const SLandingPage = styled.div(
  () => css`
    display: block;
    min-height: 100vh;
    height: 100%;
    box-sizing: border-box;
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
  `
);

const SSection = styled.div(
  () => `
  padding: 0;
  display: flex;
  justify-content: center;
`
);

const SBackgroundObjectsTopRight = styled.img<{}>(
  () => `
    position: absolute;
    height: 13vh;
    right: 14px;
    top: -10px;
`
);

const SBackgroundObjectsTopLeft = styled.img<{}>(
  () => `
    position: absolute;
    height: 5vh;
    left: 0;
`
);

const SBackgroundObjectsBottomRight = styled.img<{}>(
  () => `
    position: absolute;
    height: 13vh;
    right: 0;
    bottom: 0;
`
);

const SBackgroundObjectsBottomLeft = styled.img<{}>(
  () => `
    position: absolute;
    height: 30vh;
    left: 0;
    bottom: 0;
`
);

const STopHeader = styled.div(
  ({ theme }) => `
    background: ${theme.colors.primaryBackground};

    position: relative;
    @media only screen and ${theme.device.laptopS} {
      height: 600px;
    }
`
);

const SScrollSmallScreen = styled.div(
  ({ theme }) => `
    display: none;
    @media only screen and ${theme.device.max.laptopS} {
      display: block;
    }
  `
);

const SHowItWorks = styled.div(
  ({ theme }) => css`
    padding: 40px 15%;
    margin: 5vh 20vw 5vh 20vw;
    border-radius: ${theme.borderRadiusCommon}px;
    background: ${theme.colors.lightBackground};
    box-sizing: content-box;
    @media only screen and ${theme.device.max.laptopS} {
      margin: 0 0 50px;
      border-radius: 0;
    }
  `
);

const SWhyUs = styled.div(
  () => `
  margin: 1vh 13vw;
  padding: 60px 20px
`
);

const SBlocksContainer = styled.div(
  ({ theme }) => `
  display: inline-flex;
  @media only screen and ${theme.device.max.laptopS} {
    flex-direction: column;
  }
`
);

const SBlock = styled.div(
  ({ theme }) => `
  margin: 20px;
  @media only screen and ${theme.device.max.laptopS} {
    margin: 0;
    flex-direction: column;
  }
`
);

const STitle = styled.div(
  ({ theme }) => `
        font-family: Poppins;
        font-weight: bold;
        line-height: 22px;
        font-size: ${theme.font.size.smallMedium3};
        color: ${theme.colors.primaryBackground};
        text-align: center;
        margin: 25px;
        font-weight: 700;
        & a {
          color: ${theme.colors.primaryBright};
          font-size: ${theme.font.size.small};
        }
  `
);

const SButton = styled(OnlineButton as any)(
  ({ theme }) => css`
    font-size: ${theme.font.size.smallMedium};
    background: ${theme.colors.primaryBright};
    height: 50px;
    margin: auto;
    margin-bottom: 60px;
    min-width: 240px;
  `
);

const SBulletsLargeScreen = styled.div(
  ({ theme }) => `
  display: flex;
  justify-content: center;
  @media only screen and ${theme.device.max.laptopS} {
    display: none;
  }
`
);

const SBulletText = styled(BulletLine as any)(
  ({ theme }) => `
  max-width: 360px;
  width: 88%;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 160px;
  text-align: center;
  font-family: Poppins;
  font-weight: ${theme.font.weight.bold};
  font-size: 18px;
  line-height: 27px;
`
);

const SReady = styled.div(
  ({ theme }) => `
  max-width: 1140px;
  width: 100%;
  margin: 5vw;
  border-radius: ${theme.borderRadiusCommon}px;
  background: ${theme.colors.bulletBackground};
`
);
const SReadyTitle = styled(Title as any)`
  padding: 60px 20px;
`;

// tslint:enable:variable-name
// ############################ End Styles ###########################

interface ILandingPageProps {
  privacyPolicyUrl: string;
  termsOfUseUrl: string;
  ratesAndTermsUrl: string;
  retailerName: string;
  verticalGroup: keyof typeof Verticals;
  state: StateCodes;
  merchantAprLowerLimit: number;
  merchantAprUpperLimit: number;
  merchantOriginator: Originators;
  merchantVertical: Verticals;
  merchantOriginatorActiveStates: StateCodes[];
  applicationAmountFinancedLowerLimit: number;
  submit: () => void;
  logoUrl: string;
  isDiscountTire: boolean;
  dpUpdate?: string;
}

const LandingPage: React.FC<ILandingPageProps> = (props) => {
  const sectionRef = React.useRef<HTMLSpanElement>(null);
  const scrollDown = () => sectionRef?.current?.scrollIntoView({ behavior: 'smooth' });

  const { t } = useTranslation('landing_page');

  const { isDiscountTire, submit, retailerName, merchantVertical, dpUpdate } = props;

  const [isSupportPopupOpen, setIsSupportPopupOpen] = useState(false);

  const toggleSupportPopup = useCallback(() => {
    setIsSupportPopupOpen((state) => !state);
  }, []);

  const tipsPercent = useMemo(() => {
    if (merchantVertical === Verticals.Car_Dealerships && ['CDApproveAllV3', 'CDApproveAllV2', 'Group E', 'Group F', 'Group G'].includes(dpUpdate)) {
      return '97';
    }
    return merchantVertical === Verticals.Dental ? '85' : '90';
  }, [merchantVertical, dpUpdate]);

  const tips = isDiscountTire ? t('discount_tire_tips') : t('tips', { percents: tipsPercent });

  const howToBullets = ((isDiscountTire ? t('discount_tire_how_to_bullets') : t('how_to_bullets', { retailerName })) as (string | [string, string])[]).map(
    (step) => (Array.isArray(step) ? [step[0], <HelpText>{step[1]}</HelpText>] : step)
  );

  const createFingerBullet = ({ body, link }, key) =>
    link ? <BulletLine linkAddress={props.termsOfUseUrl} key={key} text={body} link={link} isBlankTarget={true} /> : <BulletLine key={key} text={body} />;

  const finePrintBulletsBlocks = ((isDiscountTire ? t('discount_tire_fine_print_bullets') : t('fine_print_bullets')) as { body; link }[][]).map((block) =>
    block.map(createFingerBullet)
  );

  return (
    <PrivacyAllowanceLayer>
      {isSupportPopupOpen && <SupportPopup {...{ onClose: toggleSupportPopup }} />}
      <Top {...{ headerTitleText: `Proud partners of ${retailerName}`, showCloseButton: false, onSupportClick: toggleSupportPopup }} />
      <SLandingPage>
        <STopHeader>
          <SBackgroundObjectsTopLeft src={line1} />
          <SBackgroundObjectsBottomLeft src={triangle2} />
          <SBackgroundObjectsTopRight src={triangle1} />
          <SBackgroundObjectsBottomRight src={circle1} />
          <GetStarted {...{ isDiscountTire, submit, retailerName, vertical: merchantVertical }} />
          <SScrollSmallScreen>
            <ArrowDown title="Loan details below" onArrowClick={scrollDown} />
          </SScrollSmallScreen>
        </STopHeader>
        <SBulletsLargeScreen>
          <SBulletText text={tips[0]} icon={creditCheck} />
          <SBulletText text={tips[1]} icon={thumbsUp} />
          <SBulletText text={tips[2]} icon={lightningFast} />
        </SBulletsLargeScreen>
        <SHowItWorks>
          <Title>How it works</Title>
          <ConnectedBulletsList bullets={howToBullets} />
        </SHowItWorks>
        <SButton
          {...{
            dataAt: 'get-started-how-it-works',
            submit: props.submit,
            size: ButtonSize.mediumLarge,
            buttonText: t('get_started_button'),
          }}
        />
        <Reviews submit={props.submit} />
        <SWhyUs>
          <STitle>We’re up front with the fine print:</STitle>
          <SBlocksContainer>
            {finePrintBulletsBlocks.map((finePrintBulletsBlock, blockIndex) => (
              <SBlock key={blockIndex}>{finePrintBulletsBlock.map((bullet) => bullet)}</SBlock>
            ))}
          </SBlocksContainer>
        </SWhyUs>
        <SSection>
          <SReady>
            <SReadyTitle>
              Ready to see your monthly <br /> payment options?
            </SReadyTitle>
            <SButton dataAt="get-started-ready" submit={props.submit} size={ButtonSize.mediumLarge} buttonText="Get started!" />
          </SReady>
        </SSection>
        <SSection ref={sectionRef as any}>
          <PoweredBy privacyPolicyUrl={props.privacyPolicyUrl} termsOfUseUrl={props.termsOfUseUrl} />
        </SSection>
      </SLandingPage>
    </PrivacyAllowanceLayer>
  );
};

export default LandingPage;
